<template>
  <vue-modal :key="refreshModal" :ref="modalName" :name="modalName" :clickToClose="false" :draggable="true" :adaptive="true" :resizable="true" :scrollable="false" :width="860" :maxHeight="680" :height="'auto'" @opened="loadData">
    <div class="modal-content individual-respondents-modal">
      <div class="modal-header">
        <h5 class="modal-title">Select Individuals max({{ maxSelectedRespondents }})</h5>
        <button type="button" class="close" @click="closeModal">
          <span aria-hidden="true">&times;</span>
        </button>
      </div>

      <div class="modal-body">
        <div v-if="selectionMessage != ''" class="selection-message">
          {{ selectionMessage }}
        </div>

        <div class="individual-respondents-controls">
          <div class="individual-respondents-search">
            <label for="individualRespondentsSearch" class="mr-2 mb-0">Search:</label>
            <input
              id="individualRespondentsSearch"
              v-model="searchText"
              type="text"
              class="form-control form-control-sm"
            />
          </div>
        </div>

        <div class="individual-respondents-table-container">
          <table class="table table-sm table-hover individual-respondents-table">
            <thead>
              <tr>
                <th class="select-column"></th>
                <th>
                  <button type="button" class="sort-header-button" @click="sortRespondents('dateCompleted')">
                    Date
                    <span class="sort-indicator">{{ getSortIndicator("dateCompleted") }}</span>
                  </button>
                </th>
                <th>
                  <button type="button" class="sort-header-button" @click="sortRespondents('company')">
                    Company
                    <span class="sort-indicator">{{ getSortIndicator("company") }}</span>
                  </button>
                </th>
                <th>
                  <button type="button" class="sort-header-button" @click="sortRespondents('name')">
                    Name
                    <span class="sort-indicator">{{ getSortIndicator("name") }}</span>
                  </button>
                </th>
                <th>
                  <button type="button" class="sort-header-button" @click="sortRespondents('emailAddress')">
                    Email
                    <span class="sort-indicator">{{ getSortIndicator("emailAddress") }}</span>
                  </button>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="respondent in sortedRespondents" :key="respondent.surveyTakerId">
                <td class="select-column">
                  <input
                    type="checkbox"
                    :checked="isSelected(respondent)"
                    @change="toggleRespondent(respondent, $event)"
                  />
                </td>
                <td>{{ getRespondentDate(respondent) }}</td>
                <td>{{ respondent.company }}</td>
                <td>{{ respondent.name }}</td>
                <td>{{ respondent.emailAddress }}</td>
              </tr>
              <tr v-if="sortedRespondents.length == 0">
                <td colspan="5" class="empty-message">No individual respondents to display.</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <div class="modal-footer">
        <button type="button" class="btn btn-primary" :disabled="selectedRespondents.length == 0" @click="saveSelection">Save</button>
        <button type="button" class="btn btn-primary ml-2" @click="closeModal">Close</button>
      </div>
    </div>
  </vue-modal>
</template>

<script>
export default {
  name: "IndividualRespondentsModal",
  props: {
    respondents: {
      type: Array,
      default: () => []
    },
    modalName: {
      type: String,
      default: "individual-respondents-modal"
    },
    savedSelectedRespondents: {
      type: Array,
      default: () => []
    },
  },
  data() {
    return {
      selectedRespondents: [],
      selectionMessage: "",
      maxSelectedRespondents: 30,
      refreshModal: true,
      searchText: "",
      sortColumn: "company",
      sortDirection: "asc"
    };
  },
  computed: {
    filteredRespondents() {
      const searchText = this.searchText.trim().toLowerCase();
      const respondents = JSON.parse(JSON.stringify(this.respondents));
      if (searchText == "") {
        return respondents;
      }
      return respondents.filter(respondent => {
        return [
          this.getRespondentDate(respondent),
          respondent.company,
          respondent.name,
          respondent.emailAddress
        ].some(value => this.normalizeSearchValue(value).includes(searchText));
      });
    },
    sortedRespondents() {
      return this.filteredRespondents.slice().sort((firstRespondent, secondRespondent) => this.compareRespondents(firstRespondent, secondRespondent));
    }
  },
  methods: {
    loadData() {
      if (this.savedSelectedRespondents.length > 0) {
        this.selectedRespondents = JSON.parse(JSON.stringify(this.savedSelectedRespondents));
      }
      this.selectionMessage = "";
      this.searchText = "";
    },
    sortRespondents(sortColumn) {
      if (this.sortColumn == sortColumn) {
        this.sortDirection = this.sortDirection == "asc" ? "desc" : "asc";
      } else {
        this.sortColumn = sortColumn;
        this.sortDirection = "asc";
      }
    },
    getSortIndicator(sortColumn) {
      if (this.sortColumn != sortColumn) {
        return "";
      }
      return this.sortDirection == "asc" ? "^" : "v";
    },
    compareRespondents(firstRespondent, secondRespondent) {
      if (this.sortColumn == "dateCompleted") {
        return this.compareDates(firstRespondent.dateCompleted, secondRespondent.dateCompleted);
      }
      const compareValue = this.compareStrings(firstRespondent[this.sortColumn], secondRespondent[this.sortColumn]);
      if (compareValue == 0) {
        return this.compareDatesDescending(firstRespondent.dateCompleted, secondRespondent.dateCompleted);
      } else {
        return compareValue;
      }
    },
    compareDates(firstValue, secondValue) {
      const firstTime = this.getDateTime(firstValue);
      const secondTime = this.getDateTime(secondValue);
      if (firstTime == secondTime) {
        return 0;
      }
      if (firstTime == null) {
        return 1;
      }
      if (secondTime == null) {
        return -1;
      }
      const compareValue = firstTime - secondTime;
      return this.sortDirection == "asc" ? compareValue : compareValue * -1;
    },
    compareDatesDescending(firstValue, secondValue) {
      const firstTime = this.getDateTime(firstValue);
      const secondTime = this.getDateTime(secondValue);
      if (firstTime == secondTime) {
        return 0;
      }
      if (firstTime == null) {
        return 1;
      }
      if (secondTime == null) {
        return -1;
      }
      return secondTime - firstTime;
    },
    compareStrings(firstValue, secondValue) {
      const firstString = firstValue || "";
      const secondString = secondValue || "";
      const firstIsBlank = firstString == "" || (this.sortColumn == "company" && firstString == "--");
      const secondIsBlank = secondString == "" || (this.sortColumn == "company" && secondString == "--");
      if (firstIsBlank && secondIsBlank) {
        return 0;
      }
      if (firstIsBlank) {
        return 1;
      }
      if (secondIsBlank) {
        return -1;
      }
      const compareValue = firstString.localeCompare(secondString);
      return this.sortDirection == "asc" ? compareValue : compareValue * -1;
    },
    getDateTime(value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        return date.getTime();
      }
      return null;
    },
    normalizeSearchValue(value) {
      if (value == null) {
        return "";
      }
      return String(value).toLowerCase();
    },
    isSelected(respondent) {
      return this.selectedRespondents.some(selectedRespondent => selectedRespondent.surveyTakerId == respondent.surveyTakerId);
    },
    toggleRespondent(respondent, event) {
      const checked = event.target.checked;
      if (checked == true) {
        if (this.selectedRespondents.length >= this.maxSelectedRespondents) {
          this.selectionMessage = `Only ${this.maxSelectedRespondents} respondents can be selected.`;
          event.target.checked = false;
          return;
        }
        if (!this.isSelected(respondent)) {
          this.selectedRespondents.push(JSON.parse(JSON.stringify(respondent)));
        }
        this.selectionMessage = "";
      } else {
        this.selectedRespondents = this.selectedRespondents.filter(selectedRespondent => selectedRespondent.surveyTakerId != respondent.surveyTakerId);
        this.selectionMessage = "";
      }
    },
    getRespondentDate(respondent) {
      if (respondent.dateCompleted) {
        return this.formatDateOnly(respondent.dateCompleted);
      }
      return "";
    },
    formatDateOnly(value) {
      const date = new Date(value);
      if (!isNaN(date.getTime())) {
        const month = date.toLocaleString("en-US", { month: "short", timeZone: "UTC" });
        const day = String(date.getUTCDate()).padStart(2, "0");
        const year = date.getUTCFullYear();
        return `${month}-${day}-${year}`;
      }
      return value;
    },
    saveSelection() {
      const selectedRespondents = JSON.parse(JSON.stringify(this.selectedRespondents));
      this.$emit("save", {
        respondents: selectedRespondents,
        surveyTakerIds: selectedRespondents.map(respondent => respondent.surveyTakerId)
      });
      this.closeModal();
    },
    closeModal() {
      if (this.$refs[this.modalName] != null) {
        this.$refs[this.modalName].visible = false;
      }
      this.refreshModal = !this.refreshModal;
    }
  },
  watch: {
    savedSelectedRespondents: {
      handler(newVal) {
        this.selectedRespondents = JSON.parse(JSON.stringify(newVal || []));
      },
      deep: true,
      immediate: true
    }
  }
};
</script>
